/**
 * Enhanced User Identification System
 * Creates unique, trackable user identifiers with comprehensive country/region data
 */

import { getCanonicalLocation } from "@/lib/canonicalLocation";

// Amplitude tracking removed

export interface UserProfile {
  userId: string;
  region: "LATAM" | "SPAIN" | "US" | "OTHER";
  country?: string;
  templateType: "BaseTemplate" | "BioLinkTemplate";
  userSegment: string;
  firstSeen: string;
  lastActivity: string;
  deviceFingerprint: string;
  sessionCount: number;
  conversionStage: "visitor" | "engaged" | "purchaser" | "converted";
}

/**
 * Generate a unique device fingerprint for user identification
 */
function generateDeviceFingerprint(): string {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Device fingerprint', 2, 2);
  }
  
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    !!window.sessionStorage,
    !!window.localStorage,
    navigator.platform,
    canvas.toDataURL(),
  ].join('|');
  
  // Create a simple hash
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  return Math.abs(hash).toString(36);
}

/**
 * Fallback browser-only de país para el fingerprint/perfil — corre SOLO
 * cuando el Worker no resolvió país (getCanonicalLocation devolvió null).
 * VERDAD ÚNICA: el país primario viene del Worker vía canonicalLocation.
 */
function legacyCountryFromTimezone(): string | undefined {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const browserLang = navigator.language || (navigator as any).userLanguage;
  const langCountry = browserLang.split("-")[1]?.toUpperCase();
  
  // Enhanced timezone to country mapping
  const timezoneCountryMap: Record<string, string> = {
    // Spain
    "Europe/Madrid": "ES",
    "Europe/Barcelona": "ES",
    "Atlantic/Canary": "ES",
    
    // US
    "America/New_York": "US",
    "America/Los_Angeles": "US",
    "America/Chicago": "US",
    "America/Denver": "US",
    "America/Phoenix": "US",
    "America/Anchorage": "US",
    "Pacific/Honolulu": "US",
    
    // LATAM Countries
    "America/Argentina/Buenos_Aires": "AR",
    "America/Argentina/Cordoba": "AR",
    "America/Mexico_City": "MX",
    "America/Bogota": "CO",
    "America/Santiago": "CL",
    "America/Lima": "PE",
    "America/Sao_Paulo": "BR",
    "America/Manaus": "BR",
    "America/Fortaleza": "BR",
    "America/Caracas": "VE",
    "America/Guayaquil": "EC",
    "America/Montevideo": "UY",
    "America/Asuncion": "PY",
    "America/La_Paz": "BO",
    "America/Costa_Rica": "CR",
    "America/Panama": "PA",
    "America/Guatemala": "GT",
    "America/Tegucigalpa": "HN",
    "America/El_Salvador": "SV",
    "America/Managua": "NI",
    "America/Santo_Domingo": "DO",
    "America/Havana": "CU",
    "America/Puerto_Rico": "PR",
  };
  
  // Try exact timezone match first
  if (timezoneCountryMap[timezone]) {
    return timezoneCountryMap[timezone];
  }
  
  // Try partial timezone match
  for (const [tz, country] of Object.entries(timezoneCountryMap)) {
    if (timezone.includes(tz.split("/")[1])) {
      return country;
    }
  }
  
  // Fallback to browser language
  return langCountry;
}

/**
 * Determine region from country code
 */
function getRegionFromCountry(countryCode?: string): "LATAM" | "SPAIN" | "US" | "OTHER" {
  if (!countryCode) return "LATAM"; // Default
  
  if (countryCode === "ES") return "SPAIN";
  if (countryCode === "US") return "US";
  
  const latamCountries = [
    "AR", "BO", "BR", "CL", "CO", "CR", "CU", "DO", "EC", "SV", 
    "GT", "HN", "MX", "NI", "PA", "PY", "PE", "PR", "UY", "VE"
  ];
  
  if (latamCountries.includes(countryCode)) return "LATAM";
  
  return "OTHER";
}

/**
 * Determine template type based on country and region
 */
function getTemplateType(region: string, countryCode?: string): "BaseTemplate" | "BioLinkTemplate" {
  if (region === "SPAIN" || region === "US") {
    return "BioLinkTemplate";
  }
  
  if (region === "LATAM" && countryCode) {
    // Specific LATAM countries that use BioLink template
    const bioLinkCountries = ["MX", "CL", "BR", "PY"];
    if (bioLinkCountries.includes(countryCode)) {
      return "BioLinkTemplate";
    }
  }
  
  return "BaseTemplate";
}

/**
 * Get user segment based on region and country
 */
function getUserSegment(region: string, countryCode?: string): string {
  if (region === "SPAIN") return "spain_premium";
  if (region === "US") return "us_premium";
  
  if (region === "LATAM" && countryCode) {
    const highValueCountries = ["MX", "BR", "CL", "AR"];
    if (highValueCountries.includes(countryCode)) {
      return `latam_${countryCode.toLowerCase()}_premium`;
    }
    return `latam_${countryCode.toLowerCase()}_standard`;
  }
  
  return "other_standard";
}

/**
 * Create or retrieve user profile
 */
export function createUserProfile(): UserProfile {
  const deviceFingerprint = generateDeviceFingerprint();
  // VERDAD ÚNICA: país del Worker; timezone solo si el Worker no resolvió.
  const countryCode = getCanonicalLocation().country ?? legacyCountryFromTimezone();
  const region = getRegionFromCountry(countryCode);
  const templateType = getTemplateType(region, countryCode);
  const userSegment = getUserSegment(region, countryCode);
  
  // Check for existing user profile
  const existingProfile = localStorage.getItem("user_profile");
  if (existingProfile) {
    try {
      const profile: UserProfile = JSON.parse(existingProfile);
      // Update last activity
      profile.lastActivity = new Date().toISOString();
      profile.sessionCount += 1;
      localStorage.setItem("user_profile", JSON.stringify(profile));
      return profile;
    } catch (error) {
    }
  }
  
  // Create new user profile
  const userId = `${region.toLowerCase()}_${deviceFingerprint}_${Date.now().toString(36)}`;
  const profile: UserProfile = {
    userId,
    region,
    country: countryCode,
    templateType,
    userSegment,
    firstSeen: new Date().toISOString(),
    lastActivity: new Date().toISOString(),
    deviceFingerprint,
    sessionCount: 1,
    conversionStage: "visitor",
  };
  
  // Store profile
  localStorage.setItem("user_profile", JSON.stringify(profile));
  
  return profile;
}

/**
 * Update user conversion stage
 */
export function updateConversionStage(stage: "visitor" | "engaged" | "purchaser" | "converted") {
  const profileData = localStorage.getItem("user_profile");
  if (profileData) {
    try {
      const profile: UserProfile = JSON.parse(profileData);
      profile.conversionStage = stage;
      profile.lastActivity = new Date().toISOString();
      localStorage.setItem("user_profile", JSON.stringify(profile));
      
      // Amplitude user properties removed
      
    } catch (error) {
    }
  }
}

/**
 * Initialize user tracking with comprehensive data
 */
export function initializeUserTracking() {
  const profile = createUserProfile();
  
  // Amplitude user properties removed
  
  // Amplitude user identification removed
  
  return profile;
}

/**
 * Get market tier for business intelligence
 */
function getMarketTier(region: string, countryCode?: string): "tier_1" | "tier_2" | "tier_3" {
  if (region === "SPAIN" || region === "US") return "tier_1";
  
  if (region === "LATAM" && countryCode) {
    const tier1Countries = ["MX", "BR", "CL", "AR"];
    const tier2Countries = ["CO", "PE", "UY", "PY"];
    
    if (tier1Countries.includes(countryCode)) return "tier_1";
    if (tier2Countries.includes(countryCode)) return "tier_2";
  }
  
  return "tier_3";
}

/**
 * Get current user profile
 */
export function getCurrentUserProfile(): UserProfile | null {
  const profileData = localStorage.getItem("user_profile");
  if (profileData) {
    try {
      return JSON.parse(profileData);
    } catch (error) {
    }
  }
  return null;
}

/**
 * Track user activity with enhanced data
 */
export function trackUserActivity(activityType: string, additionalData?: Record<string, unknown>) {
  const profile = getCurrentUserProfile();
  if (!profile) return;
  
  // Update last activity
  profile.lastActivity = new Date().toISOString();
  localStorage.setItem("user_profile", JSON.stringify(profile));
  
  // Track activity with user context - Amplitude tracking removed
}